import { useState, useEffect, useRef } from 'react'
import { Card, Button, Table, Badge, Row, Col, Spinner, Alert, Pagination, Form } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import { formatDateOnlyEsMX, getDateOnlySortTimestamp } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'

const FinancialTransactionsList = ({ 
  initialFilters = {},
  initialSortField = 'transaction_date',
  initialSortDirection = 'desc'
}) => {
  const { user } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterBankAccount, setFilterBankAccount] = useState(initialFilters.bank_account_id || '')
  const [filterType, setFilterType] = useState(initialFilters.transaction_type || 'all')
  const [filterReconciled, setFilterReconciled] = useState(initialFilters.is_reconciled !== undefined ? (initialFilters.is_reconciled ? 'true' : 'false') : 'all')
  const [startDate, setStartDate] = useState(initialFilters.start_date || '')
  const [endDate, setEndDate] = useState(initialFilters.end_date || '')
  
  // Ordenamiento y paginación
  const [sortField, setSortField] = useState(initialSortField)
  const [sortDirection, setSortDirection] = useState(initialSortDirection)
  
  // Ref para almacenar los valores anteriores de initialFilters y evitar loops infinitos
  const prevInitialFiltersRef = useRef(null)
  const isInitialMount = useRef(true)
  
  // Actualizar filtros cuando cambien los props iniciales (solo si realmente cambiaron)
  useEffect(() => {
    const currentFilters = initialFilters || {}
    
    // En el primer render, solo inicializar la referencia
    if (isInitialMount.current) {
      isInitialMount.current = false
      prevInitialFiltersRef.current = { ...currentFilters }
      return
    }
    
    // Comparar valores individuales para detectar cambios reales
    const prevFilters = prevInitialFiltersRef.current || {}
    const hasChanges = Object.keys(currentFilters).some(key => 
      currentFilters[key] !== prevFilters[key] && currentFilters[key] !== undefined && currentFilters[key] !== ''
    )
    
    // Solo actualizar si hay cambios reales
    if (hasChanges) {
      prevInitialFiltersRef.current = { ...currentFilters }
      
      if (currentFilters.bank_account_id !== undefined) {
        setFilterBankAccount(currentFilters.bank_account_id || '')
      }
      if (currentFilters.transaction_type !== undefined) {
        setFilterType(currentFilters.transaction_type || 'all')
      }
      if (currentFilters.is_reconciled !== undefined) {
        setFilterReconciled(currentFilters.is_reconciled ? 'true' : 'false')
      }
      if (currentFilters.start_date !== undefined) {
        setStartDate(currentFilters.start_date || '')
      }
      if (currentFilters.end_date !== undefined) {
        setEndDate(currentFilters.end_date || '')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialFilters?.bank_account_id,
    initialFilters?.transaction_type,
    initialFilters?.is_reconciled,
    initialFilters?.start_date,
    initialFilters?.end_date
  ])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [exporting, setExporting] = useState(false)

  // Obtener lista de cuentas bancarias para filtro
  const { data: bankAccounts } = useQuery(
    'bank-accounts-for-transactions-filter',
    async () => {
      const response = await api.get('/financial/bank-accounts/?is_active=true')
      return response.data.results || response.data || []
    }
  )

  // Construir query params
  const buildQueryParams = () => {
    const params = new URLSearchParams()
    if (filterBankAccount) params.append('bank_account_id', filterBankAccount)
    if (filterType !== 'all') params.append('transaction_type', filterType)
    if (filterReconciled !== 'all') params.append('is_reconciled', filterReconciled === 'true' ? 'true' : 'false')
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    params.append('ordering', sortDirection === 'desc' ? `-${sortField}` : sortField)
    return params.toString()
  }

  // Obtener lista de transacciones financieras
  const { data: transactionsData, isLoading, error } = useQuery(
    ['financial-transactions', filterBankAccount, filterType, filterReconciled, startDate, endDate, sortField, sortDirection],
    async () => {
      const queryParams = buildQueryParams()
      const response = await api.get(`/financial/financial-transactions/${queryParams ? `?${queryParams}` : ''}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Obtener resumen de transacciones financieras
  const { data: summaryData, isLoading: isLoadingSummary } = useQuery(
    ['financial-transactions-summary', filterBankAccount, filterType, filterReconciled, startDate, endDate],
    async () => {
      const params = new URLSearchParams()
      if (filterBankAccount) params.append('bank_account_id', filterBankAccount)
      if (filterType !== 'all') params.append('transaction_type', filterType)
      if (filterReconciled !== 'all') params.append('is_reconciled', filterReconciled === 'true' ? 'true' : 'false')
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      const url = `/financial/financial-transactions/summary/${params.toString() ? `?${params.toString()}` : ''}`
      const response = await api.get(url)
      return response.data
    },
    { enabled: !!user }
  )

  const handleExport = async (format) => {
    try {
      setExporting(true)
      const queryParams = buildQueryParams()
      const url = `/financial/export-financial-transactions/?${queryParams ? `${queryParams}&` : ''}format=${format}`
      
      const response = await api.get(url, {
        responseType: 'blob'
      })
      
      const url_blob = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url_blob
      link.setAttribute('download', `Transacciones_Financieras.${format === 'excel' ? 'xlsx' : 'pdf'}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success(`Exportación ${format.toUpperCase()} completada`)
    } catch (error) {
      console.error('Error exportando:', error)
      toast.error('Error al exportar transacciones financieras')
    } finally {
      setExporting(false)
    }
  }

  // Filtrar localmente por término de búsqueda
  const filteredTransactions = (transactionsData || []).filter(transaction => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      transaction.description?.toLowerCase().includes(searchLower) ||
      transaction.reference?.toLowerCase().includes(searchLower) ||
      transaction.bank_account_name?.toLowerCase().includes(searchLower)
    )
  })

  // Ordenamiento local adicional
  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]
    
    if (sortField === 'transaction_date') {
      aVal = getDateOnlySortTimestamp(aVal)
      bVal = getDateOnlySortTimestamp(bVal)
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal?.toLowerCase() || ''
    } else if (sortField === 'amount') {
      aVal = parseFloat(aVal || 0)
      bVal = parseFloat(bVal || 0)
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    }
  })

  // Paginación
  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedTransactions = sortedTransactions.slice(startIndex, endIndex)

  // Manejar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Iconos de ordenamiento
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted"></i>
    }
    return sortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-primary"></i>
      : <i className="bi bi-arrow-down text-primary"></i>
  }

  // Badge para tipo de transacción
  const getTypeBadge = (type) => {
    const badges = {
      'DEPOSIT': { bg: 'success', text: 'Depósito' },
      'WITHDRAWAL': { bg: 'danger', text: 'Retiro' },
      'TRANSFER': { bg: 'info', text: 'Transferencia' },
      'FEE': { bg: 'warning', text: 'Comisión' },
      'INTEREST': { bg: 'primary', text: 'Interés' }
    }
    const badge = badges[type] || { bg: 'secondary', text: type }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  // Calcular totales
  const totalDeposits = filteredTransactions
    .filter(t => t.transaction_type === 'DEPOSIT')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
  
  const totalWithdrawals = filteredTransactions
    .filter(t => t.transaction_type === 'WITHDRAWAL')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
  
  const netAmount = totalDeposits - totalWithdrawals

  if (error) {
    return (
      <Alert variant="danger">
        Error al cargar las transacciones financieras: {error.message}
      </Alert>
    )
  }

  return (
    <div>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Transacciones Financieras</h5>
          <div className="d-flex gap-2">
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleExport('pdf')}
              disabled={exporting || filteredTransactions.length === 0}
            >
              {exporting ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-file-pdf me-1"></i> PDF</>}
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => handleExport('excel')}
              disabled={exporting || filteredTransactions.length === 0}
            >
              {exporting ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-file-excel me-1"></i> Excel</>}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Filtros */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Descripción, referencia..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Cuenta Bancaria</Form.Label>
                <Form.Select
                  value={filterBankAccount}
                  onChange={(e) => {
                    setFilterBankAccount(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="">Todas</option>
                  {bankAccounts?.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Tipo</Form.Label>
                <Form.Select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="all">Todos</option>
                  <option value="DEPOSIT">Depósito</option>
                  <option value="WITHDRAWAL">Retiro</option>
                  <option value="TRANSFER">Transferencia</option>
                  <option value="FEE">Comisión</option>
                  <option value="INTEREST">Interés</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Estado Conciliación</Form.Label>
                <Form.Select
                  value={filterReconciled}
                  onChange={(e) => {
                    setFilterReconciled(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="all">Todos</option>
                  <option value="true">Conciliado</option>
                  <option value="false">No Conciliado</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={1.5}>
              <Form.Group>
                <Form.Label>Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={1.5}>
              <Form.Group>
                <Form.Label>Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Resumen básico siempre visible */}
          <Row className="mb-3">
            <Col md={4}>
              <Alert variant="success" className="mb-0">
                <strong>Total Depósitos:</strong> {formatCurrency(totalDeposits)}
              </Alert>
            </Col>
            <Col md={4}>
              <Alert variant="danger" className="mb-0">
                <strong>Total Retiros:</strong> {formatCurrency(totalWithdrawals)}
              </Alert>
            </Col>
            <Col md={4}>
              <Alert variant={netAmount >= 0 ? 'info' : 'warning'} className="mb-0">
                <strong>Neto:</strong> {formatCurrency(netAmount)}
              </Alert>
            </Col>
          </Row>

          {/* Resumen detallado (siempre visible) */}
          {summaryData && (
            <Card className="mb-3">
              <Card.Header>
                <strong>Resumen Detallado</strong>
              </Card.Header>
              <Card.Body>
                {isLoadingSummary ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  <>
                    {/* Totales por tipo */}
                    <Row className="mb-3">
                      <Col md={12}>
                        <h6>Totales por Tipo de Transacción</h6>
                        <Table striped bordered size="sm">
                          <thead>
                            <tr>
                              <th>Tipo</th>
                              <th className="text-end">Monto</th>
                              <th className="text-end">Cantidad</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summaryData.por_tipo_transaccion?.map((item) => (
                              <tr key={item.transaction_type}>
                                <td>{getTypeBadge(item.transaction_type)}</td>
                                <td className="text-end">
                                  {formatCurrency(item.total_amount || 0)}
                                </td>
                                <td className="text-end">{item.count || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>

                    {/* Totales por cuenta bancaria */}
                    {summaryData.por_cuenta_bancaria && summaryData.por_cuenta_bancaria.length > 0 && (
                      <Row className="mb-3">
                        <Col md={12}>
                          <h6>Totales por Cuenta Bancaria</h6>
                          <div className="table-responsive">
                            <Table striped bordered size="sm">
                              <thead>
                                <tr>
                                  <th>Cuenta Bancaria</th>
                                  <th className="text-end">Depósitos</th>
                                  <th className="text-end">Retiros</th>
                                  <th className="text-end">Transferencias</th>
                                  <th className="text-end">Cantidad</th>
                                </tr>
                              </thead>
                              <tbody>
                                {summaryData.por_cuenta_bancaria.map((item) => {
                                  return (
                                    <tr key={item.bank_account__id}>
                                      <td>{item.bank_account__name || 'N/A'}</td>
                                      <td className="text-end text-success">
                                        {formatCurrency(item.total_deposits || 0)}
                                      </td>
                                      <td className="text-end text-danger">
                                        {formatCurrency(item.total_withdrawals || 0)}
                                      </td>
                                      <td className="text-end">
                                        {formatCurrency(item.total_transfers || 0)}
                                      </td>
                                      <td className="text-end">
                                        <strong>{item.count || 0}</strong>
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </Table>
                          </div>
                        </Col>
                      </Row>
                    )}

                    {/* Totales por estado de conciliación */}
                    {summaryData.por_conciliacion && summaryData.por_conciliacion.length > 0 && (
                      <Row>
                        <Col md={12}>
                          <h6>Totales por Estado de Conciliación</h6>
                          <Table striped bordered size="sm">
                            <thead>
                              <tr>
                                <th>Estado</th>
                                <th className="text-end">Monto</th>
                                <th className="text-end">Cantidad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {summaryData.por_conciliacion.map((item) => (
                                <tr key={item.is_reconciled}>
                                  <td>
                                    {item.is_reconciled ? (
                                      <Badge bg="success">Conciliado</Badge>
                                    ) : (
                                      <Badge bg="warning">No Conciliado</Badge>
                                    )}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrency(item.total_amount || 0)}
                                  </td>
                                  <td className="text-end">{item.count || 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Tabla */}
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th 
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('transaction_date')}
                        className="position-relative"
                      >
                        <span className="d-inline-flex align-items-center gap-1">
                          Fecha
                          {getSortIcon('transaction_date')}
                        </span>
                      </th>
                      <th 
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('bank_account_name')}
                        className="position-relative"
                      >
                        <span className="d-inline-flex align-items-center gap-1">
                          Cuenta Bancaria
                          {getSortIcon('bank_account_name')}
                        </span>
                      </th>
                      <th>Tipo</th>
                      <th 
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('amount')}
                        className="position-relative"
                      >
                        <span className="d-inline-flex align-items-center gap-1">
                          Monto
                          {getSortIcon('amount')}
                        </span>
                      </th>
                      <th>Descripción</th>
                      <th>Referencia</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No se encontraron transacciones financieras
                        </td>
                      </tr>
                    ) : (
                      paginatedTransactions.map(transaction => {
                        // Determinar color de fila según tipo y estado
                        let rowClass = ''
                        if (transaction.is_reconciled) {
                          rowClass = 'table-success'
                        } else if (transaction.transaction_type === 'WITHDRAWAL') {
                          rowClass = 'table-danger-light' // Clase personalizada para retiros no conciliados
                        } else if (transaction.transaction_type === 'DEPOSIT') {
                          rowClass = 'table-success-light' // Clase personalizada para depósitos no conciliados
                        }
                        
                        return (
                          <tr key={transaction.id} className={rowClass}>
                            <td>
                              {formatDateOnlyEsMX(transaction.transaction_date, {}, '—')}
                            </td>
                            <td>
                              <strong>{transaction.bank_account_name || 'N/A'}</strong>
                            </td>
                            <td>{getTypeBadge(transaction.transaction_type)}</td>
                            <td className={`text-end ${transaction.transaction_type === 'DEPOSIT' ? 'text-success fw-bold' : transaction.transaction_type === 'WITHDRAWAL' ? 'text-danger fw-bold' : 'text-info'}`}>
                              {transaction.transaction_type === 'DEPOSIT' ? '+' : transaction.transaction_type === 'WITHDRAWAL' ? '-' : ''}
                              {formatCurrency(transaction.amount || 0)}
                            </td>
                            <td>{transaction.description || 'N/A'}</td>
                            <td>
                              <span className="font-monospace small">{transaction.reference || '-'}</span>
                            </td>
                            <td>
                              {transaction.is_reconciled ? (
                                <Badge bg="success">
                                  <i className="bi bi-check-circle me-1"></i>
                                  Conciliado
                                </Badge>
                              ) : (
                                <Badge bg="warning">
                                  <i className="bi bi-clock me-1"></i>
                                  No Conciliado
                                </Badge>
                              )}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Total:</strong></td>
                      <td>
                        <strong>
                          {formatCurrency(filteredTransactions.reduce((sum, t) => {
                            const amount = parseFloat(t.amount || 0)
                            return sum + (t.transaction_type === 'DEPOSIT' ? amount : -amount)
                          }, 0))}
                        </strong>
                      </td>
                      <td colSpan="3"></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <Form.Select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(parseInt(e.target.value))
                        setCurrentPage(1)
                      }}
                      style={{ width: 'auto', display: 'inline-block' }}
                    >
                      <option value="10">10 por página</option>
                      <option value="25">25 por página</option>
                      <option value="50">50 por página</option>
                      <option value="100">100 por página</option>
                    </Form.Select>
                  </div>
                  <Pagination>
                    <Pagination.First 
                      onClick={() => setCurrentPage(1)} 
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                      disabled={currentPage === 1}
                    />
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum
                      if (totalPages <= 5) {
                        pageNum = i + 1
                      } else if (currentPage <= 3) {
                        pageNum = i + 1
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i
                      } else {
                        pageNum = currentPage - 2 + i
                      }
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === currentPage}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      )
                    })}
                    <Pagination.Next 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => setCurrentPage(totalPages)} 
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                  <div>
                    Mostrando {startIndex + 1} - {Math.min(endIndex, sortedTransactions.length)} de {sortedTransactions.length} transacciones
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

export default FinancialTransactionsList
